/**
 * LeaderboardView — Global leaderboard for World Cup predictions.
 * Data flows through useWorldCupLeaderboardData adapter hook.
 * Renders via WorldCupLeaderboard (FlashList) to handle 1000+ participants.
 */

import React, { useMemo, useCallback, useRef, useState } from 'react';
import { View, Text, Image, Pressable, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { Lock, WifiOff } from 'lucide-react-native';
import type { FlashListRef } from '@shopify/flash-list';
import { useTheme } from '../../../src/theme/useTheme';
import { ARENA_EMPTY_MONKEY_SOURCE } from '../../../constants/assets';
import { useWorldCupLeaderboardData } from '../../../src/worldcup/hooks/useWorldCupLeaderboardData';
import { useWorldCupMatchesQuery } from '../../../src/worldcup/hooks/useWorldCupMatchesQuery';
import { useCompetitionId, useCompetitionBasePath, useCompetitionSurface } from '../../../src/competitions/contexts/CompetitionContext';
import { competitionMemberProfileRoute, competitionTabRoute } from '../../../src/competitions/helpers/competitionRoutes.helper';
import { useSupabaseAuth } from '../../../src/shared/contexts/SupabaseAuthContext';
import { PlayerRanksProvider } from '../../../src/economy/contexts/PlayerRanksContext';
import type { LeaderboardEntry } from '../../../src/worldcup/types/worldcup.types';
import { StaggeredFadeIn } from '../../shared/StaggeredFadeIn';
import { WorldCupLeaderboard } from '../WorldCupLeaderboard/WorldCupLeaderboard';
import { LeaderboardSelfStandingBar } from '../WorldCupLeaderboard/LeaderboardSelfStandingBar';
import { WorldCupSkeleton } from '../WorldCupSkeleton/WorldCupSkeleton';
import { getLeaderboardViewStyles } from './LeaderboardView.styles';
import { LeaderboardLiveRankingNote } from './LeaderboardLiveRankingNote';
import {
  getLeaderboardComingSoonSubtitle,
  LEADERBOARD_VIEW_COPY,
  LEADERBOARD_MIN_SHOW_THRESHOLD,

} from './LeaderboardView.constants';
import { LEADERBOARD_RANK_COMPACT_MAX_WIDTH } from '../WorldCupLeaderboard/LeaderboardCard.constants';
import { shouldShowCompetitionLeaderboard } from '../../../src/competitions/helpers/competitionLeaderboard.helper';

export interface LeaderboardViewProps {
  /**
   * Minimum participants before the board replaces the "coming soon" card.
   * Omitted by the World Cup route → LEADERBOARD_MIN_SHOW_THRESHOLD (50), unchanged.
   * Competition routes pass a small value so young pools show a real board.
   */
  minShowThreshold?: number;
  /** Competition name for the coming-soon copy; defaults to the WC brand. */
  competitionName?: string;
}

export const LeaderboardView: React.FC<LeaderboardViewProps> = ({
  minShowThreshold = LEADERBOARD_MIN_SHOW_THRESHOLD,
  competitionName,
}) => {
  const theme = useTheme();
  const { width: windowWidth } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const router = useRouter();
  const competitionId = useCompetitionId();
  const basePath = useCompetitionBasePath();
  /**
   * Flags on the World Cup surface, kits everywhere else (AC-3.6) — the same test, from the
   * same context field, that `useMatchPickStats` already uses to pick `crestMode` for the
   * pick-stats crest (AC-10.6). Derived here because this is the one place in the leaderboard
   * tree that knows which surface it is rendering; every other caller of `WorldCupLeaderboard`
   * keeps flags by omitting the prop.
   */
  const surface = useCompetitionSurface();
  const teamVisual = surface === 'worldcup' ? 'flag' : 'kit';
  const compactRank = windowWidth <= LEADERBOARD_RANK_COMPACT_MAX_WIDTH;
  const { isSignedIn, isLoaded } = useSupabaseAuth();

  const flashListRef = useRef<FlashListRef<LeaderboardEntry> | null>(null);

  /**
   * Global leaderboard: open participant profile (no squad context). Tapping your
   * own row opens the same profile in self mode (editable futures + an "edit my
   * picks" CTA) so the leaderboard stays consistent — every row opens a WC page.
   */
  const handleLeaderboardRowPress = useCallback(
    (entry: LeaderboardEntry) => {
      if (!entry.userId) return;
      router.push({
        pathname: competitionMemberProfileRoute(basePath) as never,
        params: {
          memberId: entry.userId,
          wcTab: 'leaderboard',
          ...(entry.isCurrentUser ? { self: '1', rank: String(entry.rank) } : {}),
        },
      } as never);
    },
    [router],
  );

  const matchesQuery = useWorldCupMatchesQuery(competitionId, isLoaded && isSignedIn);
  const futuresOptions = matchesQuery.data?.futuresOptions ?? null;

  const {
    top3,
    rest,
    currentUserEntry,
    isCurrentUserOnPage,
    leaderboard,
    isLoading: leaderboardDataLoading,
    isError,
  } = useWorldCupLeaderboardData();

  // Server withholds peers' picks until the first kickoff; show one explanatory
  // banner so the locked rows read as intentional rather than a loading glitch.
  const peerFuturesHidden = useMemo(
    () => [...top3, ...rest].some((e) => e.futuresHidden && !e.isCurrentUser),
    [top3, rest],
  );

  // When any match is in progress, explain that the global order stays on settled
  // points until full-time (only the squad leaderboard re-ranks live), so a moving
  // "+N live" on a card without the rank changing reads as intentional.
  const hasLiveGames = useMemo(
    () => [...top3, ...rest].some(
      (e) => Array.isArray(e.livePredictions) && e.livePredictions.length > 0,
    ),
    [top3, rest],
  );

  /** The signed-in viewer has a standing bar whenever they have a row (the view
   * itself only renders once there are enough participants, so no extra size gate
   * is needed). Drives the reserved bottom padding — kept constant regardless of
   * the bar's live visibility so fading it out never shifts the list. */
  const hasSelfBar = isSignedIn && currentUserEntry !== null;

  /**
   * Whether the viewer's own row is currently on screen. When it is, we hide the
   * floating bar so they don't see themselves twice. Off-page viewers (outside the
   * top 50) never have a row in the list, so this stays false and the bar stays up.
   */
  const [isSelfRowOnScreen, setIsSelfRowOnScreen] = useState(false);

  /**
   * Players whose rank/level the crests need (specs/rank-on-social-screens AC-10/AC-15).
   * The slice is capped at 50 + self server-side, so this is always well under the batch cap.
   * Mounting the provider is what turns crests on: the shared WorldCupLeaderboard tree also
   * renders the squad standings, which this slice leaves alone (spec §Non-Goals).
   */
  const rankUserIds = useMemo(
    () =>
      [...top3, ...rest, ...(currentUserEntry ? [currentUserEntry] : [])]
        .map((e) => e.userId)
        .filter((id): id is string => id != null && id !== ''),
    [top3, rest, currentUserEntry],
  );

  const styles = useMemo(
    () =>
      getLeaderboardViewStyles(theme, {
        compactRank,
        windowWidth,
        bottomInset: insets.bottom,
        reserveSelfBar: hasSelfBar,
      }),
    [theme, compactRank, windowWidth, insets.bottom, hasSelfBar],
  );

  /**
   * Standing-bar tap. On-page viewers have a real row, so scroll to it. Off-page
   * viewers have no row in the list (the hook deliberately omits them to avoid a
   * duplicate card), so the bar is their only affordance — open their profile.
   */
  const handleSelfBarPress = useCallback(() => {
    if (!currentUserEntry) return;
    if (!isCurrentUserOnPage) {
      handleLeaderboardRowPress(currentUserEntry);
      return;
    }
    flashListRef.current?.scrollToItem({ item: currentUserEntry, animated: true, viewPosition: 0.35 });
  }, [currentUserEntry, isCurrentUserOnPage, handleLeaderboardRowPress]);

  if (leaderboardDataLoading && isSignedIn) {
    return (
      <View style={styles.container}>
        <WorldCupSkeleton variant="leaderboard" />
      </View>
    );
  }

  if (isError) {
    return (
      <View style={[styles.container, styles.errorContainer]}>
        <View style={styles.errorIconWrap}>
          <WifiOff size={48} color={theme.gray[400]} />
        </View>
        <Text style={styles.errorTitle}>{LEADERBOARD_VIEW_COPY.ERROR_TITLE}</Text>
        <Text style={styles.errorSubtitle}>{LEADERBOARD_VIEW_COPY.ERROR_SUBTITLE}</Text>
        <Pressable style={styles.errorButton} onPress={() => router.replace(competitionTabRoute(basePath, 'leaderboard') as never)}>
          <Text style={styles.errorButtonLabel}>{LEADERBOARD_VIEW_COPY.ERROR_RETRY}</Text>
        </Pressable>
      </View>
    );
  }

  if (!shouldShowCompetitionLeaderboard(leaderboard.length, minShowThreshold)) {
    return (
      <View style={[styles.container, styles.comingSoonContainer]}>
        <View style={styles.comingSoonBadge}>
          <Text style={styles.comingSoonBadgeText}>{LEADERBOARD_VIEW_COPY.COMING_SOON_BADGE}</Text>
        </View>
        <Image source={ARENA_EMPTY_MONKEY_SOURCE} style={styles.comingSoonMonkey} resizeMode="contain" />
        <Text style={styles.comingSoonTitle}>{LEADERBOARD_VIEW_COPY.COMING_SOON_TITLE}</Text>
        <Text style={styles.comingSoonSubtitle}>{getLeaderboardComingSoonSubtitle(competitionName)}</Text>
      </View>
    );
  }

  return (
    <PlayerRanksProvider userIds={rankUserIds}>
    <View style={styles.container}>
      <View style={styles.mainColumn}>
        <StaggeredFadeIn index={0} style={styles.listFlex}>
          <WorldCupLeaderboard
            leaderboardGlass
            // Rows render inside a recycling FlashList; per-row native-driver
            // entrance animations re-fire on recycle and have been implicated in
            // Android 16 Fabric mount races. The container StaggeredFadeIn above
            // still provides a single mount-in for the whole list.
            disableRowEntranceAnimation
            top3={top3}
            rest={rest}
            teamVisual={teamVisual}
            contentContainerStyle={styles.listContent}
            listTopContent={
              <View style={styles.topHeader}>
                <View style={styles.topHeaderTitleRow}>
                  <View style={styles.topHeaderAccent} />
                  <Text style={styles.topHeaderTitle}>{LEADERBOARD_VIEW_COPY.TOP_HEADER_TITLE}</Text>
                </View>
                <Text style={styles.topHeaderSubtitle}>{LEADERBOARD_VIEW_COPY.TOP_HEADER_SUBTITLE}</Text>
                {peerFuturesHidden ? (
                  <View style={styles.futuresHiddenBanner}>
                    <Lock size={15} color={theme.gray[600]} />
                    <Text style={styles.futuresHiddenBannerText}>
                      {LEADERBOARD_VIEW_COPY.FUTURES_HIDDEN_BANNER}
                    </Text>
                  </View>
                ) : null}
                {hasLiveGames ? <LeaderboardLiveRankingNote /> : null}
              </View>
            }
            futuresOptions={futuresOptions}
            flashListRef={flashListRef}
            onCurrentUserVisibilityChanged={setIsSelfRowOnScreen}
            onLeaderboardRowPress={isSignedIn ? handleLeaderboardRowPress : undefined}
          />
        </StaggeredFadeIn>
      </View>

      {hasSelfBar && currentUserEntry ? (
        <LeaderboardSelfStandingBar
          entry={currentUserEntry}
          onPress={handleSelfBarPress}
          visible={!isSelfRowOnScreen}
        />
      ) : null}
    </View>
    </PlayerRanksProvider>
  );
};
